/**
 * Cart Krono Sync — bundle synchronization for the cart page.
 *
 * When a Krono bundle (main + gift) is detected in the cart:
 * 1. Groups items by `_krono_bundle` property
 * 2. Hides gift item quantity controls and remove buttons
 * 3. Disables gift product links (image + title) to prevent navigating to
 *    the gift product's detail page
 * 4. Inserts a "Bundle" header above each bundle group
 * 5. Intercepts main product quantity changes → syncs gift quantity via /cart/update.js
 * 6. Intercepts main product removal → removes both items via /cart/update.js (line keys → 0)
 * 7. Uses section-based hot-swap (no page reload) + listens for CartUpdateEvent to re-decorate
 *
 * Dependencies: cart.liquid must render `data-krono-bundle`, `data-krono-role`,
 * and `data-line-key` attributes on each cart item wrapper.
 *
 * Shopify AJAX Cart API ref: https://shopify.dev/docs/api/ajax/reference/cart
 * - /cart/update.js accepts `updates` keyed by line item key (recommended over
 *   variant_id to avoid collisions when the same variant appears in multiple bundles).
 * - Setting quantity to 0 in /cart/update.js removes the line item.
 */

(function () {
	"use strict";

	var BUNDLE_ATTR = "data-krono-bundle";
	var ROLE_ATTR = "data-krono-role";

	// =========================================================================
	// 0. Helpers: section rendering (hot-swap, no page reload)
	// =========================================================================
	function getCartSectionInfo() {
		var cartItemsEl = document.getElementById("main-cart-items");
		if (!cartItemsEl) return null;
		var sectionId = cartItemsEl.getAttribute("data-id");
		if (!sectionId) return null;
		return {
			id: "main-cart-items",
			section: sectionId,
			selector: ".js-contents",
			url: window.location.pathname,
		};
	}

	function getSectionInnerHTML(html, selector) {
		try {
			var doc = new DOMParser().parseFromString(html, "text/html");
			var el = doc.querySelector(selector);
			return el ? el.innerHTML : null;
		} catch (e) {
			return null;
		}
	}

	function extractErrorMessage(value) {
		if (typeof value === "string") return value;
		if (Array.isArray(value)) {
			return value.map(extractErrorMessage).filter(Boolean).join(", ");
		}
		if (value && typeof value === "object") {
			return (
				extractErrorMessage(value.description) ||
				extractErrorMessage(value.message) ||
				extractErrorMessage(value.error) ||
				Object.values(value)
					.map(extractErrorMessage)
					.filter(Boolean)
					.join(", ") ||
				""
			);
		}
		return value ? String(value) : "";
	}

	function handleCartResponse(res) {
		if (res.status === 429) {
			// Shopify returns the human-verification page as HTML.
			// A fetch() cannot render the challenge, so navigate to it.
			showErrorToast("Failed to update cart. Reloading page...");
			setTimeout(function () {
				window.location.reload();
			}, 1000);
			return new Promise(function () {});
		}
		if (!res.ok) {
			return res.json().then(
				function (err) {
					var msg = extractErrorMessage(err);
					throw new Error(
						msg || "Request failed (" + res.status + ")",
					);
				},
				function () {
					throw new Error("Request failed (" + res.status + ")");
				},
			);
		}
		return res.json();
	}

	function renderCartSection(state, sectionInfo) {
		if (!sectionInfo) return;
		if (!state || !state.sections) return;

		var sectionHTML = state.sections[sectionInfo.section];
		if (!sectionHTML) return;

		// Update cart items list
		var cartItemsEl = document.getElementById(sectionInfo.id);
		if (cartItemsEl) {
			var targetEl =
				cartItemsEl.querySelector(sectionInfo.selector) || cartItemsEl;
			var newContent = getSectionInnerHTML(
				sectionHTML,
				sectionInfo.selector,
			);
			if (newContent !== null) {
				targetEl.innerHTML = newContent;
			}
		}

		// Update cart footer (totals, discounts, etc.)
		var footerEl = document.getElementById("main-cart-footer");
		if (footerEl) {
			var footerTarget =
				footerEl.querySelector(".js-cart-footer-contents") || footerEl;
			var newFooterContent = getSectionInnerHTML(
				sectionHTML,
				".js-cart-footer-contents",
			);
			if (newFooterContent !== null) {
				footerTarget.innerHTML = newFooterContent;
			}
			footerEl.classList.remove(
				"bx-opacity-50",
				"bx-pointer-events-none",
			);
		}

		// Re-init dynamic checkout buttons
		if (
			window.Shopify &&
			window.Shopify.PaymentButton &&
			window.Shopify.PaymentButton.init
		) {
			window.Shopify.PaymentButton.init();
		}
	}

	// =========================================================================
	// 0.5. Loading-state helpers: freeze/unfreeze bundle row controls
	// =========================================================================
	function setRowControlsDisabled(row, disabled) {
		if (!row) return;
		row.querySelectorAll(
			"[data-quantity-decrease], [data-quantity-increase], .cart-quantity-input, [data-remove-item]",
		).forEach(function (el) {
			el.disabled = disabled;
		});
	}

	function setBundleLoading(rows, loading) {
		var opacity = loading ? "0.6" : "1";
		rows.forEach(function (row) {
			if (!row) return;
			row.style.opacity = opacity;
			setRowControlsDisabled(row, loading);
		});
	}

	// =========================================================================
	// 1. Group items by bundle ID
	// =========================================================================
	function getBundleGroups() {
		var items = document.querySelectorAll("[" + BUNDLE_ATTR + "]");
		var groups = new Map();

		items.forEach(function (el) {
			var bundleId = el.getAttribute(BUNDLE_ATTR);
			if (!bundleId) return;

			if (!groups.has(bundleId)) {
				groups.set(bundleId, { main: null, gift: null });
			}
			var group = groups.get(bundleId);
			var role = el.getAttribute(ROLE_ATTR);
			if (role === "main") group.main = el;
			if (role === "gift") group.gift = el;
		});

		// Filter out incomplete groups (must have both main and gift)
		var complete = new Map();
		groups.forEach(function (group, bundleId) {
			if (group.main && group.gift) {
				complete.set(bundleId, group);
			}
		});
		return complete;
	}

	// =========================================================================
	// 2. Decorate bundle groups: show gift quantity (readonly), insert header
	// =========================================================================
	function decorateBundleGroups() {
		var groups = getBundleGroups();

		groups.forEach(function (group, bundleId) {
			var main = group.main;
			var gift = group.gift;

			// Show gift quantity as a read-only display:
			// hide +/- SVGs, strip wrapper border + input dividers,
			// give the input its own clean single-border at full width.
			var qtyWrapper = gift.querySelector("[data-quantity-wrapper]");
			if (qtyWrapper) {
				var decreaseBtn = qtyWrapper.querySelector(
					"[data-quantity-decrease]",
				);
				var increaseBtn = qtyWrapper.querySelector(
					"[data-quantity-increase]",
				);
				if (decreaseBtn) decreaseBtn.style.display = "none";
				if (increaseBtn) increaseBtn.style.display = "none";

				// Drop the wrapperʼs outer border — the input will carry its own.
				qtyWrapper.style.border = "none";

				var qtyInput = qtyWrapper.querySelector(".cart-quantity-input");
				if (qtyInput) {
					qtyInput.setAttribute("readonly", "");

					// Replace border-0 + border-x dividers with a single
					// uniform border so it reads as one clean input.
					qtyInput.style.border = "1px solid #d1d5db";
					qtyInput.style.borderRadius = "0.5rem";
					qtyInput.style.width = "120px";
					qtyInput.style.paddingLeft = "12px";
					qtyInput.style.paddingRight = "12px";
					qtyInput.style.textAlign = "center";
				}
			}

			// Hide gift remove buttons (both mobile and desktop)
			var removeBtns = gift.querySelectorAll("[data-remove-item]");
			removeBtns.forEach(function (btn) {
				btn.style.display = "none";
			});

			// Disable product links on gift item — prevent navigating
			// to the gift product's detail page. The gift is part of a
			// bundle and should not be purchasable on its own.
			disableGiftProductLinks(gift);

			// Add lock indicator label to gift item
			var titleArea = gift.querySelector(
				".bx-flex-col.bx-justify-center",
			);
			if (
				titleArea &&
				!titleArea.querySelector(".krono-gift-lock-label")
			) {
				var giftLabel = document.createElement("span");
				giftLabel.className =
					"krono-gift-lock-label bx-text-xs bx-text-gray-400 bx-ml-2";
				giftLabel.textContent = "";
				titleArea.appendChild(giftLabel);
			}

			// Insert Bundle Header (guard against duplicates)
			// Check the immediate previous sibling — not the entire parent
			// container — so each bundle gets its own header.
			var prevEl = main.previousElementSibling;
			if (!prevEl || !prevEl.classList.contains("krono-bundle-header")) {
				var header = document.createElement("div");
				header.className =
					"krono-bundle-header bx-bg-amber-50 bx-border bx-border-amber-200 bx-rounded-t-xl bx-p-3 bx-flex bx-items-center bx-gap-2";
				var shortId = bundleId.slice(-8);
				// header.innerHTML =
				// 	'<span class="bx-text-sm bx-font-semibold bx-text-gray-900">Bundle</span><span class="bx-text-xs bx-text-gray-500">#' +
				// 	shortId +
				// 	"</span>";
				header.innerHTML =
					'<span class="bx-text-sm bx-font-semibold bx-text-gray-900">Bundle</span>';
				main.insertAdjacentElement("beforebegin", header);
			}
		});
	}

	// =========================================================================
	// 2.5. Disable gift product links — prevent click navigation to the gift
	//      product detail page. Called during decoration and after DOM swaps.
	// =========================================================================
	function disableGiftProductLinks(giftEl) {
		// Guard: don't double-bind
		if (giftEl.hasAttribute("data-krono-links-disabled")) return;
		giftEl.setAttribute("data-krono-links-disabled", "1");

		var links = giftEl.querySelectorAll("a[href]");
		links.forEach(function (link) {
			// Remove the href so the link is no longer navigable.
			// We store the original value so it can be restored if needed,
			// but in practice bundle items are always re-rendered from the
			// server as fresh DOM on any cart change.
			if (link.hasAttribute("href")) {
				link.setAttribute(
					"data-krono-orig-href",
					link.getAttribute("href"),
				);
				link.removeAttribute("href");
			}

			// Visually indicate the link is no longer interactive
			link.style.pointerEvents = "none";
			link.style.cursor = "default";
			link.classList.add("bx-pointer-events-none");
		});
	}

	// =========================================================================
	// 3. Intercept main product quantity change → sync gift quantity
	//    Uses line item keys (not variant IDs) for /cart/update.js to avoid
	//    collisions when the same variant appears in multiple bundles.
	// =========================================================================
	function interceptMainQuantityChange() {
		var groups = getBundleGroups();

		groups.forEach(function (group) {
			var main = group.main;
			var gift = group.gift;

			var qtyInput = main.querySelector(".cart-quantity-input");
			if (!qtyInput) return;

			// Guard: don't double-bind listeners
			if (qtyInput.hasAttribute("data-krono-bound")) return;
			qtyInput.setAttribute("data-krono-bound", "1");

			// Use line item keys (not variant IDs) — the same variant can
			// appear in multiple bundles when they differ by properties.
			// Shopify recommends line item keys for /cart/update.js:
			// https://shopify.dev/docs/api/ajax/reference/cart
			var mainKey = main.getAttribute("data-line-key");
			var giftKey = gift.getAttribute("data-line-key");

			if (!mainKey || !giftKey) return;

			// Intercept the + and - buttons and the input change event
			var increaseBtn = main.querySelector("[data-quantity-increase]");
			var decreaseBtn = main.querySelector("[data-quantity-decrease]");

			function handleQuantityChange(newQty) {
				var qty = parseInt(newQty, 10);
				if (isNaN(qty) || qty < 1) return;
				syncQuantity(mainKey, qty, giftKey, main, gift);
			}

			if (increaseBtn) {
				increaseBtn.addEventListener("click", function (e) {
					e.preventDefault();
					e.stopPropagation();
					var currentVal = parseInt(qtyInput.value, 10) || 1;
					var maxVal =
						parseInt(qtyInput.getAttribute("max"), 10) || 9999;
					if (currentVal < maxVal) {
						handleQuantityChange(currentVal + 1);
					}
				});
			}

			if (decreaseBtn) {
				decreaseBtn.addEventListener("click", function (e) {
					e.preventDefault();
					e.stopPropagation();
					var currentVal = parseInt(qtyInput.value, 10) || 1;
					if (currentVal > 1) {
						handleQuantityChange(currentVal - 1);
					}
				});
			}

			qtyInput.addEventListener("change", function (e) {
				e.preventDefault();
				e.stopPropagation();
				handleQuantityChange(qtyInput.value);
			});
		});
	}

	function syncQuantity(mainKey, newQty, giftKey, mainRow, giftRow) {
		var rows = [mainRow, giftRow];
		setBundleLoading(rows, true);

		var sectionInfo = getCartSectionInfo();
		var body = { updates: {} };
		// Keyed by line item key (string, not variant_id) per Shopify best practice.
		// Keys must be strings when using line item keys.
		body.updates[mainKey] = newQty;
		body.updates[giftKey] = newQty;
		if (sectionInfo) {
			body.sections = sectionInfo.section;
			body.sections_url = sectionInfo.url;
		}

		fetch("/cart/update.js", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		})
			.then(handleCartResponse)
			.then(function (state) {
				// Hot-swap cart sections (no page reload)
				renderCartSection(state, sectionInfo);
				// Re-decorate bundle groups after DOM update
				reinit();
			})
			.catch(function (error) {
				console.error("[Krono Sync] Quantity sync failed:", error);
				showErrorToast(
					error.message ||
						"Failed to sync bundle quantities. Please try again.",
				);
			})
			.finally(function () {
				// If the DOM was replaced, this operates on detached nodes
				// harmlessly. If rendering failed, restore interactivity.
				setBundleLoading(rows, false);
			});
	}

	// =========================================================================
	// 4. Intercept main product removal → also remove gift
	//    Uses a single /cart/update.js call with both line keys set to 0,
	//    instead of two sequential /cart/change.js calls with line indices.
	//    This is both simpler and avoids index-shift bugs.
	//    Ref: "You can remove items from the cart by setting the quantity to 0"
	//         https://shopify.dev/docs/api/ajax/reference/cart
	// =========================================================================
	function interceptMainRemove() {
		var groups = getBundleGroups();

		groups.forEach(function (group) {
			var main = group.main;
			var gift = group.gift;

			var mainRemoveBtns = main.querySelectorAll("[data-remove-item]");
			if (mainRemoveBtns.length === 0) return;

			// Guard: don't double-bind
			if (mainRemoveBtns[0].hasAttribute("data-krono-remove-bound"))
				return;

			var mainKey = main.getAttribute("data-line-key");
			var giftKey = gift.getAttribute("data-line-key");
			if (!mainKey || !giftKey) return;

			mainRemoveBtns.forEach(function (btn) {
				btn.setAttribute("data-krono-remove-bound", "1");

				btn.addEventListener("click", function (e) {
					e.preventDefault();
					e.stopPropagation();

					var rows = [main, gift];
					setBundleLoading(rows, true);

					var sectionInfo = getCartSectionInfo();
					var body = { updates: {} };
					// Set both line item keys to 0 — /cart/update.js
					// removes both items in a single atomic request.
					// No need for index-based /cart/change.js sequencing.
					body.updates[mainKey] = 0;
					body.updates[giftKey] = 0;
					if (sectionInfo) {
						body.sections = sectionInfo.section;
						body.sections_url = sectionInfo.url;
					}

					fetch("/cart/update.js", {
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify(body),
					})
						.then(handleCartResponse)
						.then(function (state) {
							// Hot-swap cart sections (no page reload)
							renderCartSection(state, sectionInfo);
							// Re-decorate after DOM update
							reinit();
						})
						.catch(function (error) {
							console.error(
								"[Krono Sync] Remove sync failed:",
								error,
							);
							showErrorToast(
								error.message ||
									"Failed to remove bundle items. Please try again.",
							);
						})
						.finally(function () {
							// If the DOM was replaced, this operates on detached nodes
							// harmlessly. If rendering failed, restore interactivity.
							setBundleLoading(rows, false);
						});
				});
			});
		});
	}

	// =========================================================================
	// 5. Error Toast
	// =========================================================================
	function showErrorToast(message) {
		var existing = document.querySelector(".krono-error-toast");
		if (existing) existing.remove();

		var toast = document.createElement("div");
		toast.className =
			"krono-error-toast bx-fixed bx-top-4 bx-right-4 bx-bg-red-500 bx-text-white bx-px-4 bx-py-3 bx-rounded-lg bx-shadow-lg bx-z-50 bx-text-sm";
		toast.textContent = message;
		document.body.appendChild(toast);
		setTimeout(function () {
			if (toast.parentNode) toast.parentNode.removeChild(toast);
		}, 5000);
	}

	// =========================================================================
	// 6. Reinit — called after section hot-swap to re-decorate new DOM
	// =========================================================================
	function reinit() {
		// Small delay to ensure DOM has been updated
		setTimeout(function () {
			decorateBundleGroups();
			interceptMainQuantityChange();
			interceptMainRemove();
		}, 0);
	}

	// =========================================================================
	// 7. Listen for CartManager updates (CartUpdateEvent)
	//    When CartManager does its own AJAX update, re-decorate our bundles
	// =========================================================================
	function listenCartUpdates() {
		// CartManager dispatches CartUpdateEvent with type 'cart:update' (ThemeEvents.cartUpdate)
		document.addEventListener("cart:update", function () {
			reinit();
		});
	}

	// =========================================================================
	// 8. Initialization
	// =========================================================================
	function init() {
		var groups = getBundleGroups();
		if (groups.size === 0) return;

		decorateBundleGroups();
		interceptMainQuantityChange();
		interceptMainRemove();
		listenCartUpdates();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", init);
	} else {
		init();
	}
})();
